import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import teamService from '@/services/teamService';
import type { TeamResponse } from '@/services/teamService';

interface Props {
  isOpen: boolean;
  team: { id: string; name: string } | null;
  memberCount?: number;
  otherTeams?: TeamResponse[];
  onTransferMembers?: (fromTeamId: string, toTeamId: string) => Promise<void>;
  onClose: () => void;
  onDeleted: () => void;
}

export default function DeleteTeamModal({ isOpen, team, memberCount = 0, otherTeams = [], onTransferMembers, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [transferTarget, setTransferTarget] = useState<string>('');

  const hasMembers = memberCount > 0;
  const canTransfer = hasMembers && otherTeams.length > 0;
  const mustDeleteMembers = hasMembers && otherTeams.length === 0;

  const handleDelete = async () => {
    if (!team) return;

    // Safety: if has members and can transfer, must select target
    if (canTransfer && !transferTarget) return;

    setError('');
    setIsDeleting(true);
    try {
      // Transfer members first if needed
      if (canTransfer && transferTarget && onTransferMembers) {
        await onTransferMembers(team.id, transferTarget);
      }
      await teamService.deleteTeam(team.id);
      setTransferTarget('');
      onDeleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('deleteTeam.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTransferTarget('');
      setError('');
      onClose();
    }
  };

  if (!team) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" />
            {t('deleteTeam.title')}
          </DialogTitle>
          <DialogDescription>
            {hasMembers
              ? t('deleteTeam.hasMembers', {
                  name: team.name,
                  count: memberCount,
                  defaultValue: '{{name}} tiene {{count}} miembros. ¿Qué hacemos con ellos?',
                })
              : t('deleteTeam.confirmMessage', { name: team.name })}
          </DialogDescription>
        </DialogHeader>

        {canTransfer && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('deleteTeam.transferTo', 'Transferir miembros a:')}
            </label>
            <Select value={transferTarget} onValueChange={setTransferTarget}>
              <SelectTrigger>
                <SelectValue placeholder={t('deleteTeam.selectTeam', 'Selecciona equipo destino')} />
              </SelectTrigger>
              <SelectContent>
                {otherTeams.map((t2) => (
                  <SelectItem key={t2.id} value={t2.id}>
                    {t2.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {mustDeleteMembers && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {t('deleteTeam.lastTeamWarning', 'Este es el único equipo. Los miembros serán eliminados junto con el equipo.')}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || (canTransfer && !transferTarget)}
          >
            {isDeleting
              ? t('common.deleting')
              : canTransfer
                ? t('deleteTeam.transferAndDelete', 'Transferir y eliminar')
                : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
